import React, { useState, useRef, useCallback, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
  IconButton,
  Tooltip,
  Divider,
  Slider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Switch,
  FormControlLabel,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  Undo as UndoIcon,
  Redo as RedoIcon,
  GridOn as GridIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  FitScreen as FitScreenIcon,
  Palette as PaletteIcon,
  TextFields as TextIcon,
  QrCode as QrIcon,
  Image as ImageIcon,
} from "@mui/icons-material";

const VisualBadgeEditor = ({
  elements = [],
  onElementsChange,
  onSave,
  canvasWidth = 400,
  canvasHeight = 600,
}) => {
  const [selectedElement, setSelectedElement] = useState(null);
  const [draggedElement, setDraggedElement] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [history, setHistory] = useState([elements]);
  const [historyIndex, setHistoryIndex] = useState(0);
  const [showProperties, setShowProperties] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [snapToGrid, setSnapToGrid] = useState(true);
  const [gridSize, setGridSize] = useState(20);
  const [showGrid, setShowGrid] = useState(true);
  const [zoom, setZoom] = useState(1);

  const canvasRef = useRef(null);
  const containerRef = useRef(null);

  // Data validation and cleanup
  const validateElements = useCallback((elementsArray) => {
    return elementsArray.filter((element) => {
      if (!element || typeof element !== "object") return false;
      if (!element.bound || typeof element.bound !== "object") return false;
      if (
        typeof element.bound.x !== "number" ||
        typeof element.bound.y !== "number"
      )
        return false;
      if (
        typeof element.bound.width !== "number" ||
        typeof element.bound.height !== "number"
      )
        return false;
      return true;
    });
  }, []);

  // Validate elements only on mount, not on every change
  useEffect(() => {
    if (elements && Array.isArray(elements)) {
      const validElements = validateElements(elements);
      if (validElements.length !== elements.length) {
        console.warn("Some elements were invalid and have been filtered out");
        onElementsChange(validElements);
      }
    }
  }, []); // Only run on mount

  // Undo/Redo functionality
  const canUndo = historyIndex > 0;
  const canRedo = historyIndex < history.length - 1;

  const saveToHistory = useCallback(
    (newElements) => {
      const newHistory = history.slice(0, historyIndex + 1);
      newHistory.push(newElements);
      setHistory(newHistory);
      setHistoryIndex(newHistory.length - 1);
    },
    [history, historyIndex]
  );

  const undo = useCallback(() => {
    if (canUndo) {
      setHistoryIndex(historyIndex - 1);
      onElementsChange(history[historyIndex - 1]);
    }
  }, [canUndo, historyIndex, history, onElementsChange]);

  const redo = useCallback(() => {
    if (canRedo) {
      setHistoryIndex(historyIndex + 1);
      onElementsChange(history[historyIndex + 1]);
    }
  }, [canRedo, historyIndex, history, onElementsChange]);

  // Mouse event handlers
  const handleMouseDown = useCallback(
    (e, element) => {
      e.preventDefault();
      e.stopPropagation();

      const rect = canvasRef.current.getBoundingClientRect();
      const x = (e.clientX - rect.left) / zoom;
      const y = (e.clientY - rect.top) / zoom;

      setSelectedElement(element);
      setDraggedElement(element);
      setIsDragging(true);
      setDragOffset({
        x: x - element.bound.x,
        y: y - element.bound.y,
      });
    },
    [zoom]
  );

  const handleMouseMove = useCallback(
    (e) => {
      if (!isDragging || !draggedElement) return;

      const rect = canvasRef.current.getBoundingClientRect();
      let x = (e.clientX - rect.left) / zoom;
      let y = (e.clientY - rect.top) / zoom;

      // Apply snap to grid if enabled
      if (snapToGrid) {
        x = Math.round(x / gridSize) * gridSize;
        y = Math.round(y / gridSize) * gridSize;
      }

      const newX = Math.max(
        0,
        Math.min(canvasWidth - draggedElement.bound.width, x - dragOffset.x)
      );
      const newY = Math.max(
        0,
        Math.min(canvasHeight - draggedElement.bound.height, y - dragOffset.y)
      );

      const updatedElements = elements.map((el) =>
        el.id === draggedElement.id
          ? { ...el, bound: { ...el.bound, x: newX, y: newY } }
          : el
      );

      onElementsChange(updatedElements);
    },
    [
      isDragging,
      draggedElement,
      dragOffset,
      zoom,
      canvasWidth,
      canvasHeight,
      elements,
      onElementsChange,
      snapToGrid,
      gridSize,
    ]
  );

  const handleMouseUp = useCallback(() => {
    if (isDragging) {
      setIsDragging(false);
      setDraggedElement(null);
      saveToHistory(elements);
    }
  }, [isDragging, elements, saveToHistory]);

  // Element management
  const addElement = useCallback(
    (template) => {
      const newElement = {
        id: Date.now() + Math.random(),
        type: template.type || "text",
        value: template.value || "New Text",
        bold: template.bold || false,
        size: template.size || 16,
        color: template.color || "black",
        bound: {
          x: template.bound?.x || 50,
          y: template.bound?.y || 50,
          width: template.bound?.width || 100,
          height: template.bound?.height || 20,
        },
      };

      const newElements = [...elements, newElement];
      onElementsChange(newElements);
      saveToHistory(newElements);
      setSelectedElement(newElement);
      setShowProperties(true);
    },
    [elements, onElementsChange, saveToHistory]
  );

  const deleteElement = useCallback(
    (elementId) => {
      const newElements = elements.filter((el) => el.id !== elementId);
      onElementsChange(newElements);
      saveToHistory(newElements);
      setSelectedElement(null);
      setShowProperties(false);
    },
    [elements, onElementsChange, saveToHistory]
  );

  const duplicateElement = useCallback(
    (element) => {
      const newElement = {
        ...element,
        id: Date.now() + Math.random(),
        bound: {
          ...element.bound,
          x: element.bound.x + 20,
          y: element.bound.y + 20,
        },
      };

      const newElements = [...elements, newElement];
      onElementsChange(newElements);
      saveToHistory(newElements);
    },
    [elements, onElementsChange, saveToHistory]
  );

  const updateElement = useCallback(
    (elementId, updates) => {
      const newElements = elements.map((el) =>
        el.id === elementId ? { ...el, ...updates } : el
      );
      onElementsChange(newElements);
    },
    [elements, onElementsChange]
  );

  // Template library
  const templates = [
    {
      name: "Name",
      type: "text",
      value: "$First Name $Last Name",
      bold: true,
      size: 18,
      color: "black",
      bound: { x: 50, y: 500, width: 200, height: 20 },
    },
    {
      name: "Company",
      type: "text",
      value: "$Company",
      bold: false,
      size: 14,
      color: "black",
      bound: { x: 50, y: 480, width: 200, height: 16 },
    },
    {
      name: "Title",
      type: "text",
      value: "$Title",
      bold: false,
      size: 12,
      color: "black",
      bound: { x: 50, y: 460, width: 200, height: 14 },
    },
    {
      name: "QR Code",
      type: "qr",
      value: "$QR Code URL",
      size: 80,
      bound: { x: 300, y: 400, width: 80, height: 80 },
    },
    {
      name: "Badge Type",
      type: "text",
      value: "PRINTER BADGE",
      bold: true,
      size: 16,
      color: "red",
      bound: { x: 50, y: 520, width: 150, height: 18 },
    },
  ];

  // Grid rendering
  const renderGrid = () => {
    if (!showGrid) return null;

    const lines = [];

    for (let x = 0; x <= canvasWidth; x += gridSize) {
      lines.push(
        <line
          key={`v-${x}`}
          x1={x}
          y1={0}
          x2={x}
          y2={canvasHeight}
          stroke="#e0e0e0"
          strokeWidth={0.5}
        />
      );
    }

    for (let y = 0; y <= canvasHeight; y += gridSize) {
      lines.push(
        <line
          key={`h-${y}`}
          x1={0}
          y1={y}
          x2={canvasWidth}
          y2={y}
          stroke="#e0e0e0"
          strokeWidth={0.5}
        />
      );
    }

    return lines;
  };

  // Element rendering
  const renderElement = (element) => {
    // Add safety check for element structure
    if (!element || !element.bound) {
      console.warn("Invalid element structure:", element);
      return null;
    }

    const isSelected = selectedElement?.id === element.id;
    const { x = 0, y = 0, width = 100, height = 20 } = element.bound;

    if (element.type === "text") {
      return (
        <g key={element.id}>
          <rect
            x={x}
            y={y}
            width={width}
            height={height}
            fill="transparent"
            stroke={isSelected ? "#1976d2" : "transparent"}
            strokeWidth={2}
            strokeDasharray={isSelected ? "5,5" : "none"}
            style={{ cursor: "move" }}
            onMouseDown={(e) => handleMouseDown(e, element)}
          />
          <text
            x={x + 5}
            y={y + height - 5}
            fontSize={element.size}
            fontWeight={element.bold ? "bold" : "normal"}
            fill={element.color}
            style={{ pointerEvents: "none" }}
          >
            {element.value}
          </text>
          {isSelected && (
            <g>
              {/* Resize handles */}
              <rect
                x={x + width - 5}
                y={y + height - 5}
                width={10}
                height={10}
                fill="#1976d2"
                style={{ cursor: "nw-resize" }}
              />
            </g>
          )}
        </g>
      );
    }

    if (element.type === "qr") {
      return (
        <g key={element.id}>
          <rect
            x={x}
            y={y}
            width={width}
            height={height}
            fill="transparent"
            stroke={isSelected ? "#1976d2" : "transparent"}
            strokeWidth={2}
            strokeDasharray={isSelected ? "5,5" : "none"}
            style={{ cursor: "move" }}
            onMouseDown={(e) => handleMouseDown(e, element)}
          />
          <rect
            x={x + 10}
            y={y + 10}
            width={width - 20}
            height={height - 20}
            fill="#f0f0f0"
            stroke="#ccc"
            strokeWidth={1}
          />
          <text
            x={x + width / 2}
            y={y + height / 2}
            textAnchor="middle"
            fontSize={12}
            fill="#666"
          >
            QR Code
          </text>
          {isSelected && (
            <g>
              <rect
                x={x + width - 5}
                y={y + height - 5}
                width={10}
                height={10}
                fill="#1976d2"
                style={{ cursor: "nw-resize" }}
              />
            </g>
          )}
        </g>
      );
    }

    return null;
  };

  return (
    <Box sx={{ display: "flex", height: "100%" }}>
      {/* Toolbar */}
      <Box sx={{ width: 250, borderRight: 1, borderColor: "divider", p: 2 }}>
        <Stack spacing={2}>
          {/* History Controls */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              History
            </Typography>
            <Stack direction="row" spacing={1}>
              <Tooltip title="Undo">
                <span>
                  <IconButton size="small" onClick={undo} disabled={!canUndo}>
                    <UndoIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Redo">
                <span>
                  <IconButton size="small" onClick={redo} disabled={!canRedo}>
                    <RedoIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </Box>

          <Divider />

          {/* Add Elements */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Add Elements
            </Typography>
            <Stack spacing={1}>
              <Button
                variant="outlined"
                startIcon={<TextIcon />}
                onClick={() => addElement({ type: "text" })}
                size="small"
                fullWidth
              >
                Add Text
              </Button>
              <Button
                variant="outlined"
                startIcon={<QrIcon />}
                onClick={() => addElement({ type: "qr" })}
                size="small"
                fullWidth
              >
                Add QR Code
              </Button>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => setShowTemplates(true)}
                size="small"
                fullWidth
              >
                Templates
              </Button>
            </Stack>
          </Box>

          <Divider />

          {/* Canvas Controls */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Canvas
            </Typography>
            <Stack spacing={1}>
              <FormControlLabel
                control={
                  <Switch
                    checked={showGrid}
                    onChange={(e) => setShowGrid(e.target.checked)}
                  />
                }
                label="Show Grid"
                size="small"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={snapToGrid}
                    onChange={(e) => setSnapToGrid(e.target.checked)}
                  />
                }
                label="Snap to Grid"
                size="small"
              />
              <Box>
                <Typography variant="caption">
                  Grid Size: {gridSize}px
                </Typography>
                <Slider
                  value={gridSize}
                  min={10}
                  max={50}
                  step={5}
                  onChange={(e, value) => setGridSize(value)}
                  size="small"
                />
              </Box>
              <Box>
                <Typography variant="caption">
                  Zoom: {Math.round(zoom * 100)}%
                </Typography>
                <Slider
                  value={zoom}
                  min={0.5}
                  max={2}
                  step={0.1}
                  onChange={(e, value) => setZoom(value)}
                  size="small"
                />
              </Box>
            </Stack>
          </Box>

          <Divider />

          {/* Element List */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Elements ({elements.length})
            </Typography>
            <Stack spacing={1} sx={{ maxHeight: 200, overflow: "auto" }}>
              {elements
                .filter((el) => el && el.bound)
                .map((element, index) => (
                  <Paper
                    key={element.id}
                    variant="outlined"
                    sx={{
                      p: 1,
                      cursor: "pointer",
                      bgcolor:
                        selectedElement?.id === element.id
                          ? "primary.light"
                          : "background.paper",
                      "&:hover": { bgcolor: "action.hover" },
                    }}
                    onClick={() => {
                      setSelectedElement(element);
                      setShowProperties(true);
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1}>
                      {element.type === "text" ? (
                        <TextIcon fontSize="small" />
                      ) : (
                        <QrIcon fontSize="small" />
                      )}
                      <Typography variant="caption" noWrap>
                        {element.type === "text" ? element.value : "QR Code"}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteElement(element.id);
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Paper>
                ))}
            </Stack>
          </Box>

          <Divider />

          {/* Actions */}
          <Box>
            <Stack spacing={1}>
              <Button
                variant="contained"
                onClick={onSave}
                startIcon={<AddIcon />}
                fullWidth
                size="small"
              >
                Save Design
              </Button>
            </Stack>
          </Box>
        </Stack>
      </Box>

      {/* Canvas Area */}
      <Box sx={{ flex: 1, position: "relative", overflow: "auto" }}>
        <Box
          ref={containerRef}
          sx={{
            position: "relative",
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
            p: 2,
          }}
        >
          <Paper
            ref={canvasRef}
            variant="outlined"
            sx={{
              position: "relative",
              width: canvasWidth * zoom,
              height: canvasHeight * zoom,
              bgcolor: "white",
              cursor: isDragging ? "grabbing" : "grab",
            }}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
          >
            <svg
              width={canvasWidth * zoom}
              height={canvasHeight * zoom}
              style={{ position: "absolute", top: 0, left: 0 }}
            >
              {renderGrid()}
              {elements.filter((el) => el && el.bound).map(renderElement)}
            </svg>
          </Paper>
        </Box>
      </Box>

      {/* Properties Panel */}
      {showProperties && selectedElement && (
        <Box sx={{ width: 300, borderLeft: 1, borderColor: "divider", p: 2 }}>
          <Stack spacing={2}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="subtitle2">Properties</Typography>
              <IconButton size="small" onClick={() => setShowProperties(false)}>
                <DeleteIcon />
              </IconButton>
            </Box>

            <Divider />

            {/* Element Type */}
            <FormControl fullWidth size="small">
              <InputLabel>Type</InputLabel>
              <Select
                value={selectedElement.type}
                onChange={(e) =>
                  updateElement(selectedElement.id, { type: e.target.value })
                }
              >
                <MenuItem value="text">Text</MenuItem>
                <MenuItem value="qr">QR Code</MenuItem>
              </Select>
            </FormControl>

            {/* Text Properties */}
            {selectedElement.type === "text" && (
              <>
                <TextField
                  label="Text"
                  value={selectedElement.value}
                  onChange={(e) =>
                    updateElement(selectedElement.id, { value: e.target.value })
                  }
                  fullWidth
                  size="small"
                  multiline
                  rows={2}
                />

                <Box>
                  <Typography variant="caption">Font Size</Typography>
                  <Slider
                    value={selectedElement.size}
                    min={8}
                    max={48}
                    onChange={(e, value) =>
                      updateElement(selectedElement.id, { size: value })
                    }
                    size="small"
                  />
                </Box>

                <FormControlLabel
                  control={
                    <Switch
                      checked={selectedElement.bold}
                      onChange={(e) =>
                        updateElement(selectedElement.id, {
                          bold: e.target.checked,
                        })
                      }
                    />
                  }
                  label="Bold"
                />

                <FormControl fullWidth size="small">
                  <InputLabel>Color</InputLabel>
                  <Select
                    value={selectedElement.color}
                    onChange={(e) =>
                      updateElement(selectedElement.id, {
                        color: e.target.value,
                      })
                    }
                  >
                    <MenuItem value="black">Black</MenuItem>
                    <MenuItem value="red">Red</MenuItem>
                    <MenuItem value="blue">Blue</MenuItem>
                    <MenuItem value="green">Green</MenuItem>
                  </Select>
                </FormControl>
              </>
            )}

            {/* QR Code Properties */}
            {selectedElement.type === "qr" && (
              <TextField
                label="QR Code URL"
                value={selectedElement.value}
                onChange={(e) =>
                  updateElement(selectedElement.id, { value: e.target.value })
                }
                fullWidth
                size="small"
              />
            )}

            <Divider />

            {/* Position */}
            <Box>
              <Typography variant="caption">Position</Typography>
              <Stack direction="row" spacing={1}>
                <TextField
                  label="X"
                  type="number"
                  value={Math.round(selectedElement.bound.x)}
                  onChange={(e) =>
                    updateElement(selectedElement.id, {
                      bound: {
                        ...selectedElement.bound,
                        x: parseInt(e.target.value) || 0,
                      },
                    })
                  }
                  size="small"
                  sx={{ flex: 1 }}
                />
                <TextField
                  label="Y"
                  type="number"
                  value={Math.round(selectedElement.bound.y)}
                  onChange={(e) =>
                    updateElement(selectedElement.id, {
                      bound: {
                        ...selectedElement.bound,
                        y: parseInt(e.target.value) || 0,
                      },
                    })
                  }
                  size="small"
                  sx={{ flex: 1 }}
                />
              </Stack>
            </Box>

            {/* Size */}
            <Box>
              <Typography variant="caption">Size</Typography>
              <Stack direction="row" spacing={1}>
                <TextField
                  label="Width"
                  type="number"
                  value={Math.round(selectedElement.bound.width)}
                  onChange={(e) =>
                    updateElement(selectedElement.id, {
                      bound: {
                        ...selectedElement.bound,
                        width: parseInt(e.target.value) || 0,
                      },
                    })
                  }
                  size="small"
                  sx={{ flex: 1 }}
                />
                <TextField
                  label="Height"
                  type="number"
                  value={Math.round(selectedElement.bound.height)}
                  onChange={(e) =>
                    updateElement(selectedElement.id, {
                      bound: {
                        ...selectedElement.bound,
                        height: parseInt(e.target.value) || 0,
                      },
                    })
                  }
                  size="small"
                  sx={{ flex: 1 }}
                />
              </Stack>
            </Box>

            <Divider />

            {/* Actions */}
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                startIcon={<CopyIcon />}
                onClick={() => duplicateElement(selectedElement)}
                size="small"
                sx={{ flex: 1 }}
              >
                Duplicate
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => {
                  deleteElement(selectedElement.id);
                  setShowProperties(false);
                }}
                size="small"
                sx={{ flex: 1 }}
              >
                Delete
              </Button>
            </Stack>
          </Stack>
        </Box>
      )}

      {/* Template Dialog */}
      <Dialog
        open={showTemplates}
        onClose={() => setShowTemplates(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Element Templates</DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
              gap: 2,
              mt: 1,
            }}
          >
            {templates.map((template, index) => (
              <Paper
                key={index}
                variant="outlined"
                sx={{
                  p: 2,
                  cursor: "pointer",
                  "&:hover": { bgcolor: "action.hover" },
                }}
                onClick={() => {
                  addElement(template);
                  setShowTemplates(false);
                }}
              >
                <Stack spacing={1}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {template.type === "text" ? (
                      <TextIcon fontSize="small" />
                    ) : (
                      <QrIcon fontSize="small" />
                    )}
                    <Typography variant="subtitle2">{template.name}</Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {template.type === "text" ? template.value : "QR Code"}
                  </Typography>
                </Stack>
              </Paper>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowTemplates(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VisualBadgeEditor;
